import {
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type Ref,
} from "react";
import { touchScreen } from "../../lib/touch";
import type { Call, Channel, CustomerDetail, DeliveryWarning, FreeOffer, LineRequest, Menu } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatPhone } from "../../lib/phone";
import { Fold } from "../../ui/Fold";
import type { Cart, CartAction, CartLine } from "./cart";
import { customerSummary, kmText } from "./customer";
import { CustomerFields, type CustomerFieldsHandle } from "./CustomerFields";
import { describeModifier, noteText } from "./modifiers";
import { shownVariant } from "./quick-entry";
import type { QuoteState } from "./useQuote";

interface Props {
  /** The order being edited (its code and total before the edit); the kind of order cannot change then. */
  editing: { code: string; totalCents: number } | null;
  /** "Stoppen" in the edit bar: nothing is changed. */
  onStopEdit: () => void;
  cart: Cart;
  dispatch: Dispatch<CartAction>;
  menu: Menu;
  quote: QuoteState;
  onChannel: (channel: Channel) => void;
  onChooseTable: () => void;
  onMoveTable: () => void;
  /** Gratis on the selected line; the sale screen asks the code the first time. */
  onToggleFree: (line: CartLine) => void;
  /** "Actie: … gratis toevoegen": the free product of the action, without a code. */
  onAddFree: (offer: FreeOffer) => void;
  newKey: () => string;
  onEdit: (line: CartLine) => void;
  /** "Herhaal" on the customer card: the last order's lines onto the ticket. */
  onRepeat: (lines: LineRequest[]) => void;
  /** "Overnemen" of the call ringing now. */
  onTakeCall: (call: Call) => void;
  /** The customer fields, to put the cursor in a missing one. */
  customerRef: Ref<CustomerFieldsHandle>;
  /** "Bellerslijst" next to the customer on a phone (above the menu on a till pc). */
  onCalls: () => void;
  children: ReactNode;
}

const CHANNELS: Channel[] = ["takeaway", "delivery", "table"];

/** The left half of the sale screen: kind of order, customer, lines and totals. */
export function Ticket({
  editing,
  onStopEdit,
  cart,
  dispatch,
  menu,
  quote,
  onChannel,
  onChooseTable,
  onMoveTable,
  onToggleFree,
  onAddFree,
  newKey,
  onEdit,
  onRepeat,
  onTakeCall,
  customerRef,
  children,
  onCalls,
}: Props) {
  const selected = cart.lines.find((line) => line.key === cart.selected) ?? null;
  const { incomingCall, callHandled, delivery, soldOut } = useTill();
  // "Gratis product" (Acties): once the order reaches the minimum, a red bar above the line buttons until it is
  // chosen; the order cannot be paid or ordered before (Furkan, 20/10/2026)
  const hasFreeLine = cart.lines.some((line) => line.promotion === true);
  const offer = pendingFreeOffer(quote, hasFreeLine, soldOut);
  const editingCode = editing?.code ?? null;
  const [empty, hint, first, or, second, end] = t.sale.empty;

  /**
   * A ticket with products folds its customer block away (Furkan, 03/10/2026): a delivery to a known
   * customer fills most of the ticket, and the room is for the products. A bar with the kind of order
   * and the customer stays; a tap on it opens the block, and the next product folds it again. Everything
   * typed is kept: the block stays where it is, out of sight. A table has no such block.
   */
  const foldable = cart.channel !== "table" && cart.lines.length > 0;
  const [opened, setOpened] = useState(false);
  const pieces = cart.lines.reduce((sum, line) => sum + line.quantity, 0);
  const [counted, setCounted] = useState(pieces);
  if (pieces !== counted) {
    setCounted(pieces);
    if (pieces > counted) setOpened(false);
  }
  const folded = foldable && !opened;
  const blockId = useId();
  /** The customer of the number on the ticket, as the fields looked it up: for the note in the bar. */
  const [known, setKnown] = useState<CustomerDetail | null>(null);
  const summary = customerSummary(cart, delivery);

  // a field that is still missing gets the cursor (the sale screen asks); a folded block opens first
  const fields = useRef<CustomerFieldsHandle>(null);
  const wanted = useRef<Parameters<CustomerFieldsHandle["focus"]>[0] | null>(null);
  useImperativeHandle(
    customerRef,
    () => ({
      focus: (field) => {
        if (!folded) {
          fields.current?.focus(field);
          return;
        }
        wanted.current = field;
        setOpened(true);
      },
    }),
    [folded],
  );
  useEffect(() => {
    if (folded || wanted.current === null) return;
    fields.current?.focus(wanted.current);
    wanted.current = null;
  }, [folded]);

  const call = incomingCall && (
    // Nummerherkenning (prototype): the call ringing now, on every till
    <div className="incoming" role="status">
      <span>
        ☎ <b>{formatPhone(incomingCall.phone)}</b> ·{" "}
        {incomingCall.customer
          ? [incomingCall.customer.name, t.calls.orders(incomingCall.customer.orders)].filter(Boolean).join(" · ")
          : t.calls.unknown}
      </span>
      <span className="acts">
        <button
          type="button"
          className="btn primary"
          onClick={() => {
            onTakeCall(incomingCall);
          }}
        >
          {t.calls.take}
        </button>
        <button type="button" className="btn ghost" onClick={callHandled}>
          {t.calls.skip}
        </button>
      </span>
    </div>
  );

  return (
    <section className="ticket" aria-label="Bestelling">
      {editing !== null && (
        // prototype: "✎ Bestelling X3K bewerken · was € 24,10" with Stoppen
        <div className="editbar" role="status">
          <span>
            {t.editing.barWas[0]} <b className="code">{editing.code}</b>{" "}
            {t.editing.barWas[1](formatEuro(editing.totalCents))}
          </span>
          <button type="button" className="btn ghost" onClick={onStopEdit}>
            {t.editing.stop}
          </button>
        </div>
      )}
      <Fold open={foldable}>
        <button
          type="button"
          className="custbar"
          aria-expanded={!folded}
          aria-controls={blockId}
          title={folded ? t.sale.customerBar.show : t.sale.customerBar.hide}
          onClick={() => {
            setOpened(!opened);
          }}
        >
          <span className="cb-kind">{t.sale.channels[cart.channel]}</span>
          <span className="cb-who">
            <span>
              {summary.who ? <b>{summary.who}</b> : t.sale.customerBar.nobody}
              {summary.phone && <> · {summary.phone}</>}
            </span>
            {summary.where !== null && <span>{summary.where}</span>}
            {summary.addressMissing && <span className="warn">{t.sale.customerBar.noAddress}</span>}
          </span>
          <span className="cb-chev" aria-hidden="true" />
          {/* the note of the customer stays in sight: it says what to do with this very order */}
          {known !== null && known.note !== "" && <span className="cb-note">{known.note}</span>}
        </button>
        {folded && call && <div className="custcall">{call}</div>}
      </Fold>

      <Fold open={!folded} id={blockId}>
        <div className="channels" role="group" aria-label="Soort bestelling">
          {CHANNELS.map((channel) => (
            <button
              key={channel}
              type="button"
              aria-pressed={cart.channel === channel}
              disabled={editingCode !== null && cart.channel !== channel}
              onClick={() => {
                // who changes the kind of order is at work in this block: it stays open until the next product
                setOpened(true);
                onChannel(channel);
              }}
            >
              {t.sale.channels[channel]}
            </button>
          ))}
        </div>

        <div className="cust">
          {!folded && call}
          {cart.channel === "table" ? (
            cart.table === "" ? (
              <button type="button" className="btn primary" onClick={onChooseTable}>
                {t.tables.choose}
              </button>
            ) : (
              <div className="tablechip">
                <span>{t.tables.label(cart.table)}</span>
                <span className="spacer" />
                {cart.tableOrder && editingCode === null && (
                  <button type="button" className="btn ghost small" onClick={onMoveTable}>
                    {t.tables.move}
                  </button>
                )}
                {editingCode === null && (
                  <button type="button" className="btn ghost small" onClick={onChooseTable}>
                    {t.tables.other}
                  </button>
                )}
              </div>
            )
          ) : (
            <>
              {/* a phone: who called is chosen where the customer is filled in, not above the menu (Furkan,
                  18/10/2026); a till pc keeps it above the menu with F10 */}
              <button type="button" className="btn ghost calls-here" onClick={onCalls}>
                {t.calls.list}
              </button>
              <CustomerFields ref={fields} cart={cart} dispatch={dispatch} onRepeat={onRepeat} onKnown={setKnown} />
            </>
          )}
        </div>
      </Fold>

      <div className="lines" aria-label="Producten">
        {cart.lines.length === 0 && (
          <p className="empty">
            {empty}
            <br />
            {touchScreen() ? (
              t.sale.emptyTouch[1]
            ) : (
              <>
                {hint}
                <span className="code">{first}</span>
                {or}
                <span className="code">{second}</span>
                {end}
              </>
            )}
          </p>
        )}
        {cart.lines.map((line) => {
          const priced = quote.priced(line);
          const product = menu.products[line.productId];
          const name = priced?.name ?? product?.name ?? line.productId;
          const variant = priced ? priced.variant : shownVariant(product, line);
          return (
            <button
              key={line.key}
              type="button"
              className="line"
              aria-pressed={line.key === cart.selected}
              onClick={() => {
                dispatch({ type: "select", key: line.key });
              }}
              onDoubleClick={() => {
                onEdit(line);
              }}
            >
              <span className="q num">{line.quantity}×</span>
              <span className="n">
                {name}
                {variant !== null && ` ${variant}`}
              </span>
              <span className="p num">
                {line.free === true ? (
                  <span className="free">{t.sale.freeLabel}</span>
                ) : priced ? (
                  formatEuro(priced.lineCents)
                ) : (
                  <span className="pending">…</span>
                )}
              </span>
              {priced && (priced.modifiers.length > 0 || priced.note !== null) && (
                <span className="mods">
                  {priced.modifiers.map((modifier, index) => (
                    <span key={index}>
                      {describeModifier(modifier)}
                      <br />
                    </span>
                  ))}
                  {priced.note !== null && noteText(priced.note)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {offer && (
        <button
          type="button"
          className="freebar"
          onClick={() => {
            onAddFree(offer);
          }}
        >
          {t.promotions.chooseFree(offer.variant === null ? offer.name : `${offer.name} ${offer.variant}`)}
        </button>
      )}

      <div className="lineactions">
        <button
          type="button"
          disabled={!selected}
          onClick={() => selected && dispatch({ type: "changeQuantity", key: selected.key, delta: -1 })}
        >
          {t.sale.minus}
        </button>
        <button
          type="button"
          disabled={!selected || selected.promotion === true}
          onClick={() => selected && dispatch({ type: "changeQuantity", key: selected.key, delta: 1 })}
        >
          {t.sale.plus}
        </button>
        <button
          type="button"
          aria-pressed={selected?.free === true}
          disabled={!selected || selected.promotion === true}
          onClick={() => selected && onToggleFree(selected)}
        >
          {t.sale.free}
          <kbd>Shift+G</kbd>
        </button>
        <button
          type="button"
          disabled={!selected || selected.promotion === true}
          onClick={() => selected && dispatch({ type: "duplicate", key: selected.key, newKey: newKey() })}
        >
          {t.sale.copy}
          <kbd>F9</kbd>
        </button>
        <button
          type="button"
          disabled={!selected}
          onClick={() => selected && dispatch({ type: "remove", key: selected.key })}
        >
          {t.sale.remove}
          <kbd>Del</kbd>
        </button>
      </div>

      <Totals quote={quote} channel={cart.channel} />
      {children}
    </section>
  );
}

/** The red warning of a delivery, as in the prototype (the order can still be saved). */
function warningText(warning: DeliveryWarning): string {
  switch (warning.code) {
    case "zone_unknown":
      return t.delivery.notHere(warning.zone);
    case "below_minimum":
      return t.delivery.minimum(warning.zone, formatEuro(warning.minimumCents));
    case "outside_area":
      return t.delivery.outside(warning.street, kmText(warning.maxKm));
    case "outside_hours":
      return t.delivery.closed;
  }
}

/**
 * The free product of the action "Gratis product" still to be chosen: the order reached the minimum, it is not on
 * the ticket yet and it is not sold out. Null otherwise.
 */
export function pendingFreeOffer(
  quote: QuoteState,
  hasFreeLine: boolean,
  soldOut: ReadonlySet<string>,
): FreeOffer | null {
  const offer = quote.fresh && !hasFreeLine ? (quote.quote?.freeOffer ?? null) : null;
  return offer !== null && !soldOut.has(offer.productId) ? offer : null;
}

function Totals({ quote, channel }: { quote: QuoteState; channel: Channel }) {
  const { promotions } = useTill();
  const amounts = quote.quote;
  const manualCents = amounts ? amounts.discountCents - amounts.promotionCents - amounts.rewardCents : 0;
  const vatRows = amounts?.vat ?? [];
  return (
    <div className="totals" aria-live="polite">
      {quote.error && <p className="error-text">{quote.error.message}</p>}
      {amounts && (
        <div className={quote.fresh ? "" : "stale"}>
          <div className="t">
            <span>{t.sale.subtotal}</span>
            <span className="num">{formatEuro(amounts.subtotalCents)}</span>
          </div>
          {channel === "delivery" && (
            <div className="t">
              <span>{t.delivery.fee}</span>
              <span className="num">
                {amounts.deliveryCents > 0 ? formatEuro(amounts.deliveryCents) : t.delivery.free}
              </span>
            </div>
          )}
          {amounts.promotionCents > 0 && promotions && (
            <div className="t promo">
              <span>{t.promotions.autoLine(promotions.auto.percent, formatEuro(promotions.auto.minimumCents))}</span>
              <span className="num">−{formatEuro(amounts.promotionCents)}</span>
            </div>
          )}
          {manualCents > 0 && (
            <div className="t">
              <span>{t.sale.discount}</span>
              <span className="num">−{formatEuro(manualCents)}</span>
            </div>
          )}
          {amounts.rewardCents > 0 && (
            <div className="t promo">
              <span>{t.loyalty.line}</span>
              <span className="num">−{formatEuro(amounts.rewardCents)}</span>
            </div>
          )}
          {channel === "delivery" &&
            (amounts.delivery?.warnings ?? []).map((warning) => (
              <div className="t warn" key={warning.code} role="alert">
                <span>{warningText(warning)}</span>
              </div>
            ))}
          {vatRows.map((vat, index) => (
            <div className="t" key={vat.rate}>
              <span>
                {t.sale.vat(vat.rate)}: {formatEuro(vat.vatCents)}
              </span>
              {index === vatRows.length - 1 && amounts.points > 0 && <span>{t.promotions.points(amounts.points)}</span>}
            </div>
          ))}
          {vatRows.length === 0 && amounts.points > 0 && (
            <div className="t">
              <span />
              <span>{t.promotions.points(amounts.points)}</span>
            </div>
          )}
        </div>
      )}
      <div className="t grand">
        <span>{t.sale.total}</span>
        <span className={`num ${quote.fresh ? "" : "stale"}`}>
          {amounts ? formatEuro(amounts.totalCents) : formatEuro(0)}
        </span>
      </div>
    </div>
  );
}
